import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { localErrorsMessages } from "../../constants/errors";
import { InputFormElement } from "../../components/UIComponents/InputFormElement";
import { newPasswordValidationConditions } from "../PasswordForm/consts";
import { modalsActions } from "../../slices/modalsSlice";
import { AllowedCharsModalContent } from "./AllowedCharsModalContent";
import { ValidationSchema } from "../../utils/useValidation";
import { FormLayout } from "../../components/FormLayout/FormLayout";
import { AuthorizationCodeInput } from "../../components/AuthorizationCodeInput/AuthorizationCodeInput";
import { userActions } from "../../slices/userSlice";

const checkAllowedCharsFn = (phrase: string) =>
  /^[a-z\d\-!$%^&*()"'/\\_+|~=`{}[:;<>?,.@#\]]*$/gi.test(phrase);

export enum DocumentPasswordFormMode {
  create = "create",
  change = "change",
}

const modesData = {
  [DocumentPasswordFormMode.create]: {
    titleTrKey: "documentPassword.create.title",
    formTitleTrKey: "documentPassword.create.form.title",
  },
  [DocumentPasswordFormMode.change]: {
    titleTrKey: "documentPassword.change.title",
    formTitleTrKey: "documentPassword.change.form.title",
  },
};

interface DocumentPasswordFormProps {
  mode: DocumentPasswordFormMode;
}

export const DocumentPasswordForm = ({ mode }: DocumentPasswordFormProps) => {
  const dispatch = useAppDispatch();
  const { errorTrKey, formData } = useAppSelector((state) => state.user);
  const showAllowedCharsModal = () =>
    dispatch(
      modalsActions.show({
        Component: AllowedCharsModalContent,
        buttons: {
          confirm: { textTrKey: "button.ok" },
        },
      })
    );
  const selectedMode = modesData[mode];

  const initialState = {
    authorizationCode: "",
    newPassword: "",
    confirmedNewPassword: "",
  };
  const validationSchema: ValidationSchema<typeof initialState> = useMemo(
    () => ({
      authorizationCode: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey,
          validate: (value) =>
            errorTrKey !== localErrorsMessages["007"] || formData.authorizationCode !== value,
          triggerEvent: "change",
        },
      ],
      newPassword: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey: "documentPassword.validation.invalidChar",
          validate: checkAllowedCharsFn,
          triggerEvent: "change",
        },
        {
          errorTrKey,
          validate: () => errorTrKey !== localErrorsMessages["013"],
          triggerEvent: "change",
        },
      ],
      confirmedNewPassword: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey: "documentPassword.validation.passwordsNotMatch",
          validate: (value, allValues) => value === "" || value === allValues.newPassword,
          triggerEvent: "blur",
        },
      ],
    }),
    [errorTrKey, formData]
  );

  return (
    <FormLayout
      titleTrKey={selectedMode.titleTrKey}
      submitTrKey="button.save"
      initialState={initialState}
      validationSchema={validationSchema}
      validator={{
        titleTrKey: "passwordForm.passwordConditionsTitle",
        conditions: newPasswordValidationConditions,
        phraseFieldId: "newPassword",
      }}
      onSubmitAction={(fieldsValue) =>
        userActions.documentPasswordConfirm({
          data: { password: fieldsValue.newPassword, authToken: fieldsValue.authorizationCode },
          mode,
        })
      }
      leftTitleTrKey="registerForm.enterOneTimepassword"
      leftComponent={(props) => {
        return (
          <AuthorizationCodeInput
            {...props}
            onResendAction={() => userActions.documentPasswordInit()}
          />
        );
      }}
      rightTitleTrKey={selectedMode.formTitleTrKey}
      rightComponent={({
        fieldsValue,
        onFieldValueChange,
        onFieldValueBlur,
        hasValidationError,
        getValidationMessagesTrKeys,
      }) => {
        const newPasswordValidationMessagesTrKeys = getValidationMessagesTrKeys("newPassword");
        return (
          <>
            <InputFormElement
              labelProps={{
                labelTrKey: "passwordForm.newPasswordPlaceholder",
              }}
              inputProps={{
                value: fieldsValue.newPassword,
                onChange: onFieldValueChange("newPassword", "confirmedNewPassword"),
                onBlur: onFieldValueBlur("newPassword"),
                isInvalid: hasValidationError("newPassword"),
                inputType: "password",
                validationMessagesTrKeys: newPasswordValidationMessagesTrKeys,
                onValidationIconClick: newPasswordValidationMessagesTrKeys?.some(
                  (v) => v.trKey === "documentPassword.validation.invalidChar"
                )
                  ? showAllowedCharsModal
                  : undefined,
              }}
            />
            <InputFormElement
              labelProps={{
                labelTrKey: "passwordForm.repeatNewPasswordPlaceholder",
              }}
              inputProps={{
                value: fieldsValue.confirmedNewPassword,
                onChange: onFieldValueChange("confirmedNewPassword", "newPassword"),
                onBlur: onFieldValueBlur("confirmedNewPassword"),
                isInvalid: hasValidationError("confirmedNewPassword"),
                inputType: "password",
                validationMessagesTrKeys: getValidationMessagesTrKeys("confirmedNewPassword"),
              }}
            />
          </>
        );
      }}
    />
  );
};
